import React from 'react'
import _ from 'underscore'
import { Check, ChevronDown, ExternalLink, Handshake, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import Modal from '../../../modal'
import store from '../../../../state/store'

const MISSED_REASON_LABEL = {
  forgot: 'tutor forgot',
  loadshedding: 'loadshedding',
  other: 'other'
}

function meetingBadge(meeting) {
  if (!meeting) return null

  if (meeting.tutor_outcome?.status === 'didnt_happen') {
    const reason = meeting.tutor_outcome.missed_reason
    return {
      icon: '!',
      colorClass: 'bg-red-600',
      tooltip: `Tutor reported the meeting didn't happen${reason ? ` (${MISSED_REASON_LABEL[reason] || reason})` : ''}`
    }
  }
  if (meeting.customer_outcome?.status === 'didnt_happen') {
    return { icon: '!', colorClass: 'bg-red-600', tooltip: "Customer reported the meeting didn't happen" }
  }
  if (meeting.tutor_outcome?.status === 'happened' || meeting.customer_outcome?.status === 'happened') {
    return { icon: '✓', colorClass: 'bg-emerald-600', tooltip: 'Meeting completed' }
  }
  if (meeting.missed) {
    return { icon: '!', colorClass: 'bg-red-600', tooltip: 'Tutor missed the meeting' }
  }
  if (new Date(meeting.end_time) < new Date()) {
    return { icon: '…', colorClass: 'bg-amber-600', tooltip: 'Awaiting feedback from the meeting' }
  }
  return { icon: '•', colorClass: 'bg-blue-600', tooltip: `Meeting scheduled for ${new Date(meeting.start_time).toLocaleString()}` }
}

class TutoringJobPositions extends React.Component {

  onRejectAllProposed = () => {
    store.emit('tutoring_job:tutor_results:reject_all_proposed', {
      tutoring_job_id: this.props.tutoring_job.id
    })
  }

  onRejectAllApplied = () => {
    store.emit('tutoring_job:tutor_results:reject_all_applied', {
      tutoring_job_id: this.props.tutoring_job.id
    })
  }

  onAcceptCustomerSelections = (notify_tutors) => {
    store.emit('tutoring_job:tutor_selection:accept', {
      tutoring_job_id: this.props.tutoring_job.id,
      notify_tutors: notify_tutors
    })
  }

  state = { proposalToRemove: null }

  onRemoveProposal = (tutor_proposal) => {
    this.setState({ proposalToRemove: tutor_proposal })
  }

  onConfirmRemoveProposal = () => {
    const tutor_proposal = this.state.proposalToRemove
    if (!tutor_proposal) return

    store.emit('tutor_proposals:remove', {
      tutor_proposal_id: tutor_proposal.id,
      tutoring_job_id: this.props.tutoring_job.id
    })
    this.setState({ proposalToRemove: null })
  }

  onCancelRemoveProposal = () => {
    this.setState({ proposalToRemove: null })
  }

  renderTutorAvatar(tutor) {
    if (!tutor) return null
    return (
      <img
        src={tutor.profile_picture_url}
        title={tutor.name}
        alt={tutor.name}
        className="mr-1 inline-block size-8 rounded-full object-cover"
      />
    )
  }

  renderProposalAvatar(tutor_proposal) {
    const tutor = tutor_proposal.tutor
    if (!tutor) return null

    const badge = meetingBadge(tutor_proposal.latest_meeting)
    const title = [tutor.name, badge?.tooltip].filter(Boolean).join(' — ')

    return (
      <span className="relative inline-block mr-1">
        <img
          src={tutor.profile_picture_url}
          title={title}
          alt={tutor.name}
          className="block w-8 h-8 rounded-full object-cover"
        />
        {badge && (
          <span
            title={badge.tooltip}
            className={`absolute -right-0.5 -bottom-0.5 w-3.5 h-3.5 rounded-full border-2 border-white text-white text-[9px] leading-[10px] text-center font-bold ${badge.colorClass}`}
          >
            {badge.icon}
          </span>
        )}
      </span>
    )
  }

  render() {
    const tutoring_job_positions = this.props.tutoring_job.tutoring_job_positions
    const tutor_proposals = this.props.tutoring_job.tutor_proposals

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="size-4"/>
            Placement
          </CardTitle>
          <CardDescription>To notify specific tutors, use the TUTORS tab per tutor result.</CardDescription>
          {this.props.tutoring_job.links?.tutor_selection ? (
            <CardAction className="ml-auto self-center">
              <a
                href={this.props.tutoring_job.links.tutor_selection}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary no-underline hover:underline"
              >
                View latest tutor selection <ExternalLink className="size-3.5"/>
              </a>
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent>
          <table className="data-table data-table--compact mb-0 text-[13px]">
            <thead>
              <tr>
                <th>Subject</th>
                <th className="text-center">Placing</th>
                <th className="text-center">Proposed</th>
                <th className="text-center">Customer Selection</th>
                <th className="text-center">Current Tutor</th>
              </tr>
            </thead>
            <tbody>
              {tutoring_job_positions.length === 0 ?
                <tr>
                  <td colSpan="5" className="py-4 text-center">
                    <Badge variant="warning" className="uppercase">No Positions</Badge>
                  </td>
                </tr>
              :
                tutoring_job_positions.map((position) => {
                  const proposals = tutor_proposals.filter(tp => tp.tutoring_job_position_id === position.id)

                  return (
                    <tr key={position.id}>
                      <td className="align-middle">
                        {position.subject_name}
                        {position.proposing_tutors &&
                          <Badge variant="success" className="ml-2 text-[10px] uppercase">Looking for tutor</Badge>
                        }
                        {position.tutor_selection_feedback &&
                          <div className="mt-1 text-xs text-muted-foreground">
                            <strong>Feedback:</strong> {position.tutor_selection_feedback}
                          </div>
                        }
                      </td>
                      <td className="text-center align-middle">
                        {position.position_open ?
                          <Check strokeWidth={3.5} className="inline size-4 text-[#1e854c]"/>
                        :
                          <X className="inline size-3.5 text-muted-foreground"/>
                        }
                      </td>
                      <td className="text-center align-middle">
                        {proposals.map((tp) =>
                          <span key={tp.id} className="cursor-pointer" onClick={() => this.onRemoveProposal(tp)}>
                            {this.renderProposalAvatar(tp)}
                          </span>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        {this.renderTutorAvatar(position.tutor_selection_tutor)}
                      </td>
                      <td className="text-center align-middle">
                        {this.renderTutorAvatar(position.tutor)}
                      </td>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </CardContent>
        <CardFooter>
          <div className="flex w-full flex-wrap items-center justify-end gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="destructive" size="sm">
                  Reject all proposed Tutors <ChevronDown className="size-3.5"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={this.onRejectAllProposed}>Reject</DropdownMenuItem>
                <DropdownMenuItem>Cancel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="destructive" size="sm">
                  Reject all Applied Tutors <ChevronDown className="size-3.5"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={this.onRejectAllApplied}>Reject</DropdownMenuItem>
                <DropdownMenuItem>Cancel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="success" size="sm">
                  Accept all Customer Selections <ChevronDown className="size-3.5"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => this.onAcceptCustomerSelections(true)}>Accept & notify tutors</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => this.onAcceptCustomerSelections(false)}>Accept only</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardFooter>
        {this.state.proposalToRemove &&
          <Modal title="Remove tutor from proposal?" size="modal-md" onClose={this.onCancelRemoveProposal}>
            <div className="p-6">
              <p className="mb-4">
                Remove <strong>{this.state.proposalToRemove.tutor?.name || 'this tutor'}</strong> from this proposal?
              </p>
              <p className="mb-4 text-[13px] text-muted-foreground">
                The customer will no longer see them in their tutor selection.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={this.onCancelRemoveProposal}>Cancel</Button>
                <Button variant="destructive" size="sm" onClick={this.onConfirmRemoveProposal}>Remove</Button>
              </div>
            </div>
          </Modal>
        }
      </Card>
    )
  }
}

export default TutoringJobPositions
